import { useState, useEffect } from 'react';
import { TableContainer, Box } from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import { InvoiceFileProcessingDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum, getStatusLabel } from 'src/utils/utility';
import IconTrue from 'src/assets/images/icons/tick-circle.svg';
import IconFalse from 'src/assets/images/icons/close-circle.svg';
import IconDocument from 'src/assets/images/icons/document.svg';
import { newFormatDateString } from 'src/utils/date';
import StatusChip from '../dashboard/StatusChip';
import { TableMain } from '../actionDetails/noRateCard/TableChargesShipment';
import { TableWrapper } from '../analytics/table/TableDetail';
import { IInternalTableFilesProps, ROW_ACTION_TYPE, SORTING_DIRECTION } from './types';
import { ButtonRowAction } from './ButtonRowAction';
import { simplifyFileNames } from '../../molecules/uploadInvoice/tools';
import { ResolutionStatusChip } from './ResolutionStatusChip';

// eslint-disable-next-line react-refresh/only-export-components
export const useInternalTableFiles = (tableOptions: MRT_TableOptions<InvoiceFileProcessingDto>) =>
  useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableRowSelection: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableSortingRemoval: false,
    enableEditing: true,
    editDisplayMode: 'table',
    muiTableHeadCellProps: {
      align: 'center',
    },
    muiTableBodyCellProps: {
      align: 'center',
    },
    ...tableOptions,
  });

// eslint-disable-next-line react-refresh/only-export-components
export const useInternalColumnsIssue = (): MRT_ColumnDef<InvoiceFileProcessingDto>[] => {
  const columnList = [
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'createdOn',
      accessorFn: (row: InvoiceFileProcessingDto) => <>{newFormatDateString(row?.createdOn)}</>,
      header: 'Created On',
      size: 100,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'updatedOn',
      accessorFn: (row: InvoiceFileProcessingDto) => <>{newFormatDateString(row?.updatedOn)}</>,
      header: 'Last Update On',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorFn: (row: InvoiceFileProcessingDto) => getStatusLabel(row?.status),
      accessorKey: 'status',
      header: 'Pipeline Status',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorFn: (row: InvoiceFileProcessingDto) => beautifyEnum(row?.statusDetails),
      accessorKey: 'statusDetails',
      header: 'Status Details',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorFn: undefined,
      accessorKey: 'id',
      header: 'Processing ID',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorFn: undefined,
      accessorKey: 'batchId',
      header: 'Batch ID',
      size: 300,
    },
    {
      enableEditing: false,
      enableSorting: false,
      accessorKey: 'errorMessage',
      accessorFn: (row: InvoiceFileProcessingDto) => <ButtonRowAction data={row} actionType={ROW_ACTION_TYPE.ERROR_MESSAGE} />,
      header: 'Error Message',
      size: 200,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'fileName',
      accessorFn: (row: InvoiceFileProcessingDto) => (
        <Box display='flex' alignItems='center' justifyContent='center' gap='8px'>
          <img src={IconDocument} alt='Document Icon' width={20} height={20} />
          <span>{simplifyFileNames(row?.fileName)}</span>
        </Box>
      ),
      header: 'File Name',
      size: 200,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'extractedPage',
      accessorFn: undefined,
      header: 'Extracted Pages',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'modelUsed',
      accessorFn: undefined,
      header: 'Model Used',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'transformerUsed',
      accessorFn: undefined,
      header: 'Transformer Used',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'invoiceNumber',
      accessorFn: undefined,
      header: 'Invoice No.',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'invoiceStatus',
      accessorFn: (row: InvoiceFileProcessingDto) => (row?.invoiceStatus ? <StatusChip status={row?.invoiceStatus} /> : <></>),
      header: 'Invoice Status.',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'resolutionStatus',
      accessorFn: (row: InvoiceFileProcessingDto) => (row?.resolutionStatus ? <ResolutionStatusChip status={row?.resolutionStatus} /> : <></>),
      header: 'Resolution Status.',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'serviceProviderName',
      accessorFn: (row: InvoiceFileProcessingDto) => beautifyEnum(row?.serviceProviderName),
      header: 'Service Provider',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'modality',
      accessorFn: undefined,
      header: 'Modality',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'billedEntity',
      accessorFn: undefined,
      header: 'Billed Entity',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'isBilled',
      accessorFn: (row: InvoiceFileProcessingDto) =>
        row.isBilled ? (
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            True <img src={IconTrue} alt='Check Mark' style={{ width: '20px', height: '20px', marginLeft: '8px' }} />
          </p>
        ) : (
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            False <img src={IconFalse} alt='Cross Mark' style={{ width: '20px', height: '20px', marginLeft: '8px' }} />
          </p>
        ),
      header: 'Credit Debited',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'lastApprovalEmailSentOn',
      accessorFn: (row: InvoiceFileProcessingDto) =>
        row?.lastApprovalEmailSentOn ? (
          <>
            <img src={IconTrue} alt='Check Mark' style={{ width: '20px', height: '20px', marginLeft: '8px' }} />
            {newFormatDateString(row?.lastApprovalEmailSentOn)}
          </>
        ) : (
          <>
            <img src={IconFalse} className='mx-auto' alt='Cross Mark' style={{ width: '20px', height: '20px' }} />
          </>
        ),
      header: 'Approval Email Sent',
      size: 100,
    },
  ];

  return columnList;
};

export const InternalTableFiles = (props: IInternalTableFilesProps) => {
  const [sorting, setSorting] = useState([{ id: 'createdOn', desc: true }]);

  const { data, onSorting, isLoading } = props;
  const columns = useInternalColumnsIssue();

  useEffect(() => {
    const runSorting = (sortingConfig) => {
      if (sortingConfig.length > 0) {
        const config = sortingConfig[0];
        const method = config?.desc ? SORTING_DIRECTION.DESC : SORTING_DIRECTION.ASC;
        onSorting(method, config?.id);
      } else {
        onSorting(SORTING_DIRECTION.DESC, 'createdOn');
      }
    };
    runSorting(sorting);
  }, [sorting, onSorting]);

  const table = useInternalTableFiles({
    columns,
    data,
    getRowId: (row: InvoiceFileProcessingDto) => row?.id,
    enableSorting: true,
    manualSorting: true,
    initialState: {
      columnPinning: { right: ['actions'] },
    },
    state: {
      sorting,
      isLoading,
    },
    onSortingChange: setSorting,
    renderEmptyRowsFallback: () => <p className='text-center text-16 font-medium uppercase text-neutral-40 py-30'>NO DATA AVAILABLE</p>,
  });

  return (
    <div className='w-full'>
      <TableContainer className='rounded-b-2xl' component={TableWrapper}>
        <TableMain>
          <MaterialReactTable table={table} />
        </TableMain>
      </TableContainer>
    </div>
  );
};
